import { useDrawer } from '@/components/common/DrawerManager';
import { FlexRow } from '@/components/common/Flex';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Box } from '@chakra-ui/react';
import {
    CashflowTransactionEntry,
    SortDirection,
} from '@common/react/cashflow';
import { getLastViewedTransactionEntryIdForSession } from '@common/react/sessionTracker';
import { DateRange } from '@common/types';
import { useState } from 'react';
import { ListChildComponentProps } from 'react-window';
import AccountCell from './AccountCell';
import AmountCell from './AmountCell';
import CategoryCell from './CategoryCell';
import DateCell from './DateCell';
import MerchantCell from './MerchantCell';
import TagsCell from './TagsCell';
import { SortKey } from './TransactionEntriesTable';
import { showTransactionEntryDetailsModal } from './TransactionEntryDetailsModal';
import ViewTransactionCell from './ViewTransactionCell';

const CLASS_NAME = 'transaction-table-row';

const TableRow = ({
    index,
    style,
    data,
}: ListChildComponentProps<{
    transactionEntries: CashflowTransactionEntry[];
    query: string | null;
    lastViewedTransactionEntryId: string | null | undefined;
    sortKey: SortKey;
    sortDirection: SortDirection;
    areFiltersApplied: boolean;
    dateRange: DateRange;
}>) => {
    const { showDrawer } = useDrawer();
    const [isEditing, setEditing] = useState<boolean>(false);

    const {
        transactionEntries,
        query,
        lastViewedTransactionEntryId,
        sortKey,
        sortDirection,
        areFiltersApplied,
        dateRange,
    } = data;

    const sessionLastViewedId = getLastViewedTransactionEntryIdForSession(
        lastViewedTransactionEntryId
    );

    const transactionEntry = transactionEntries[index];
    const nextTransactionEntry = transactionEntries[index + 1];

    const showLastViewed =
        sortKey === SortKey.Date &&
        sortDirection === SortDirection.Desc &&
        !areFiltersApplied &&
        nextTransactionEntry &&
        nextTransactionEntry.id === sessionLastViewedId;

    return (
        <FlexRow
            className={CLASS_NAME}
            style={style}
            key={transactionEntry.id}
            px={2}
            pt="-2px"
            borderBottom="1px solid"
            borderColor={showLastViewed ? 'red.400' : 'border'}
            fontSize="md"
            role="group"
            cursor="pointer"
            onClick={(e) => {
                // This is necessary because a click event on the row is firing for some reason when the category/merchant dropdown is open and then clicked out of
                // This ensures that click actually happened on a child of the row
                const parent = (e.target as HTMLElement).closest(
                    `.${CLASS_NAME}`
                );
                if (parent) {
                    showTransactionEntryDetailsModal({
                        transactionEntry,
                        showDrawer,
                    });
                }
            }}
            {...hoverStyleProps({ bg: 'blue.50' })}
            bg={
                isEditing
                    ? 'blue.50'
                    : transactionEntry.isHidden
                      ? 'gray.50'
                      : 'transparent'
            }
        >
            {showLastViewed && (
                <FlexRow
                    position="absolute"
                    right={0}
                    bottom="-11px"
                    bg="red.50"
                    color="red.400"
                    borderRadius="md"
                    px={2}
                    py="2px"
                    fontFamily="mono"
                    fontSize="sm"
                    zIndex={1}
                >
                    LAST VIEWED
                </FlexRow>
            )}

            {COLUMNS.map(({ key, component, width, minWidth }) => {
                const Component = component;

                return (
                    <Box
                        key={`${transactionEntry.id}-${key}`}
                        pt={4}
                        width={width}
                        minWidth={minWidth}
                        textAlign={
                            key === 'amount' || key === 'viewTransaction'
                                ? 'right'
                                : 'left'
                        }
                    >
                        <Component
                            transactionEntry={transactionEntry}
                            onEdit={setEditing}
                            query={query}
                            dateRange={dateRange}
                        />
                    </Box>
                );
            })}
        </FlexRow>
    );
};

export default TableRow;

export type RenderCellProps = {
    transactionEntry: CashflowTransactionEntry;
    onEdit: (isEditing: boolean) => void;
    query: string | null;
    dateRange: DateRange;
};

export const COLUMNS = [
    {
        key: 'account',
        label: 'Acc',
        width: '10%',
        component: AccountCell,
    },
    {
        key: 'date',
        label: 'Date',
        width: '15%',
        minWidth: 170,
        component: DateCell,
    },
    {
        key: 'merchant',
        label: 'Merchant',
        width: '25%',
        component: MerchantCell,
    },
    {
        key: 'category',
        label: 'Subcategory',
        width: '30%',
        component: CategoryCell,
    },
    {
        key: 'tags',
        label: '',
        width: '10%',
        minWidth: 130,
        component: TagsCell,
    },
    {
        key: 'amount',
        label: 'Amount',
        width: '5%',
        component: AmountCell,
    },
    {
        key: 'viewTransaction',
        width: '5%',
        component: ViewTransactionCell,
    },
];
